import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Laptop, Smartphone } from "lucide-react";
import { authClient } from "../lib/auth-client";
import { Button, Sticker, toastManager } from "../components/ui";
import { SettingsHeader } from "../components/SettingsHeader";
import { useSessionUser } from "../lib/useSessionUser";
import { describeDevice, isMobileDevice } from "../lib/device";
import { SUPPORT_EMAIL } from "../lib/legal";
import { OAUTH_PROVIDERS, connectedProviders } from "../lib/signInMethods";

export const Route = createFileRoute("/settings/security")({
  component: SecuritySettings,
});

const SESSIONS_KEY = ["auth-sessions"];
const ACCOUNTS_KEY = ["auth-accounts"];

interface AuthSession {
  id: string;
  token: string;
  userAgent?: string | null;
  ipAddress?: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
  /** Set on a support "view as user" session; never shown to the user. */
  impersonatedBy?: string | null;
}

function useAuthSessions() {
  return useQuery({
    queryKey: SESSIONS_KEY,
    queryFn: async () => {
      const { data, error } = await authClient.listSessions();
      if (error) throw new Error(error.message ?? "list_sessions_failed");
      return (data as AuthSession[]).filter((s) => !s.impersonatedBy);
    },
  });
}

function useConnectedProviders() {
  return useQuery({
    queryKey: ACCOUNTS_KEY,
    queryFn: async () => {
      const { data, error } = await authClient.listAccounts();
      if (error) throw new Error(error.message ?? "list_accounts_failed");
      return connectedProviders(data);
    },
  });
}

function SecuritySettings() {
  const queryClient = useQueryClient();
  const sessionUser = useSessionUser().data;
  const current = sessionUser?.session.id;
  const sessions = useAuthSessions();

  const revoke = useMutation({
    mutationFn: async (token: string | "others") => {
      const { error } =
        token === "others"
          ? await authClient.revokeOtherSessions()
          : await authClient.revokeSession({ token });
      if (error) throw new Error(error.message ?? "revoke_failed");
    },
    onSuccess: (_, token) => {
      void queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
      toastManager.add({
        type: "success",
        title:
          token === "others"
            ? "Signed out of every other device"
            : "Device signed out",
      });
    },
    onError: () =>
      toastManager.add({ type: "error", title: "Could not sign that out" }),
  });

  // This browser first, then the most recently used.
  const list = [...(sessions.data ?? [])].sort(
    (a, b) =>
      Number(b.id === current) - Number(a.id === current) ||
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
  const others = list.filter((s) => s.id !== current).length;

  return (
    <div>
      <SettingsHeader title="Security">
        How you sign in, and where. Sign out anything you don't recognise.
      </SettingsHeader>

      <SignInMethods email={sessionUser?.user.email} />

      <section className="tactile mt-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base text-ink">Signed-in devices</h3>
          {others > 0 && (
            <Button
              variant="outline"
              size="sm"
              loading={revoke.isPending && revoke.variables === "others"}
              disabled={revoke.isPending}
              onClick={() => revoke.mutate("others")}
            >
              Sign out all other devices
            </Button>
          )}
        </div>

        {sessions.isLoading ? (
          <p className="py-8 text-center text-sm font-semibold text-ink-soft">
            Loading…
          </p>
        ) : sessions.isError ? (
          <p className="py-8 text-center text-sm font-semibold text-ink-soft">
            Could not load your devices. Try again in a moment.
          </p>
        ) : (
          <ul className="mt-3 divide-y-2 divide-line">
            {list.map((s) => {
              const isCurrent = s.id === current;
              const Icon = isMobileDevice(s.userAgent) ? Smartphone : Laptop;
              return (
                <li key={s.id} className="flex items-center gap-4 py-3">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border-2 border-line bg-paper">
                    <Icon className="size-5 text-ink-soft" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-ink">
                      {describeDevice(s.userAgent)}
                      {isCurrent && (
                        <Sticker tone="mint" size="sm">
                          This device
                        </Sticker>
                      )}
                    </p>
                    <p className="text-xs font-semibold text-ink-faint">
                      {isCurrent
                        ? `Signed in ${formatDate(s.createdAt)}`
                        : `Last active ${formatDate(s.updatedAt)} · signed in ${formatDate(s.createdAt)}`}
                    </p>
                  </div>
                  {!isCurrent && (
                    <Button
                      variant="outline"
                      size="sm"
                      loading={revoke.isPending && revoke.variables === s.token}
                      disabled={revoke.isPending}
                      onClick={() => revoke.mutate(s.token)}
                    >
                      Sign out
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <p className="mt-4 text-xs font-semibold text-ink-faint">
        You sign in with Google or GitHub, so your password and two-step
        verification are managed there, not in SVG Mascot.
      </p>
    </div>
  );
}

/**
 * Read-only on purpose: one email is one account. Signing in with the other
 * provider under the same email links it; anything else goes through support.
 */
function SignInMethods({ email }: { email: string | undefined }) {
  const providers = useConnectedProviders();

  return (
    <section className="tactile p-4 sm:p-6">
      <h3 className="text-base text-ink">Sign-in methods</h3>
      <p className="mt-1 text-sm font-semibold text-ink-soft">
        Account email{" "}
        <span className="break-all font-bold text-ink">{email ?? "…"}</span>
      </p>

      {providers.isLoading ? (
        <p className="py-6 text-center text-sm font-semibold text-ink-soft">
          Loading…
        </p>
      ) : providers.isError ? (
        <p className="py-6 text-center text-sm font-semibold text-ink-soft">
          Could not load your sign-in methods. Try again in a moment.
        </p>
      ) : (
        <ul className="mt-3 divide-y-2 divide-line">
          {OAUTH_PROVIDERS.map(({ id, name }) => {
            const connected = providers.data?.has(id) ?? false;
            return (
              <li key={id} className="flex items-center gap-4 py-3">
                <p className="min-w-0 flex-1 text-sm font-bold text-ink">
                  {name}
                </p>
                <Sticker tone={connected ? "mint" : "neutral"} size="sm">
                  {connected ? "Connected" : "Not connected"}
                </Sticker>
              </li>
            );
          })}
        </ul>
      )}

      <p className="mt-3 text-sm font-semibold text-ink-soft">
        To add Google or GitHub, sign in with it using this same email. If that
        account uses a different email, signing in with it opens a separate SVG
        Mascot account, so contact{" "}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="font-bold text-ink underline"
        >
          {SUPPORT_EMAIL}
        </a>{" "}
        instead.
      </p>
    </section>
  );
}

function formatDate(value: string | Date): string {
  return new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
